'use client';

import type { Dispatch, SetStateAction } from 'react';
import type { i18n as I18n, TFunction } from 'i18next';
import { Check, ChevronDown, Hash, X } from 'lucide-react';
import { swissCantonLabel } from '@/lib/countries';
import { cn } from '@/lib/utils';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import {
  FacetCount,
  FIELD_LABEL_CLASS,
  FIELD_PLACEHOLDER_CLASS,
  FilterLabel,
  INPUT_PLACEHOLDER_CLASS,
} from '../FilterControls';
import { FilterGroup } from '../FilterGroup';
import { deFold, REGISTER_TYPE_OPTIONS } from '../searchSidebarOptions';

interface RegisterFilterGroupProps {
  t: TFunction<'translation', undefined>;
  registerCount: number;
  clearRegister: () => void;
  openFilterGroup: string | null;
  handleGroupOpenChange: (id: string) => (next: boolean) => void;
  groupLock: (group: string) => 'user' | 'pro' | null;
  currentEuid: string;
  setCurrentEuid: Dispatch<SetStateAction<string>>;
  handleFormSubmit: () => void;
  currentRegisterId: string;
  setCurrentRegisterId: Dispatch<SetStateAction<string>>;
  registerCourtOpen: boolean;
  setRegisterCourtOpen: Dispatch<SetStateAction<boolean>>;
  setRegisterCourtSearch: Dispatch<SetStateAction<string>>;
  currentRegisterCourt: string;
  i18n: I18n;
  setCurrentRegisterCourt: Dispatch<SetStateAction<string>>;
  registerCourtSearch: string;
  courtList: string[];
  facetCount: (facet: string, value: string) => number | undefined;
  registerTypeOpen: boolean;
  setRegisterTypeOpen: Dispatch<SetStateAction<boolean>>;
  currentRegisterType: string;
  setCurrentRegisterType: Dispatch<SetStateAction<string>>;
  registerTypeOptions: { value: string; label: string }[];
  leiCode: string;
  setLeiCode: Dispatch<SetStateAction<string>>;
  lockOf: (control: string) => 'user' | 'pro' | null;
  ustIdnr: string;
  setUstIdnr: Dispatch<SetStateAction<string>>;
}

export function RegisterFilterGroup({
  t,
  registerCount,
  clearRegister,
  openFilterGroup,
  handleGroupOpenChange,
  groupLock,
  currentEuid,
  setCurrentEuid,
  handleFormSubmit,
  currentRegisterId,
  setCurrentRegisterId,
  registerCourtOpen,
  setRegisterCourtOpen,
  setRegisterCourtSearch,
  currentRegisterCourt,
  i18n,
  setCurrentRegisterCourt,
  registerCourtSearch,
  courtList,
  facetCount,
  registerTypeOpen,
  setRegisterTypeOpen,
  currentRegisterType,
  setCurrentRegisterType,
  registerTypeOptions,
  leiCode,
  setLeiCode,
  lockOf,
  ustIdnr,
  setUstIdnr,
}: RegisterFilterGroupProps) {
  return (
    <FilterGroup
      id="register"
      icon={Hash}
      label={t('search.sidebar.groups.register', {
        defaultValue: 'Register & identifiers',
      })}
      activeCount={registerCount}
      onClear={clearRegister}
      open={openFilterGroup === 'register'}
      onOpenChange={handleGroupOpenChange('register')}
      lock={groupLock('register')}
    >
      {/* euId — direct lookup */}
      <div className="space-y-1.5">
        <Label className={FIELD_LABEL_CLASS}>
          {t('search.sidebar.euid', {
            defaultValue: 'European Unique Identifier',
          })}
        </Label>
        <Input
          value={currentEuid}
          onChange={(e) => setCurrentEuid(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              handleFormSubmit();
            }
          }}
          placeholder={t('search.sidebar.euidPlaceholder')}
          className={`h-11 lg:h-9 text-base lg:text-sm font-mono ${INPUT_PLACEHOLDER_CLASS}`}
        />
      </div>

      {/* Register ID — digits only. The HR* prefix lives in
                `registerType`; the numeric tail is what the backend matches
                against `register_id`. `inputMode="numeric"` brings up the
                phone keypad on mobile. */}
      <div className="space-y-1.5">
        <Label className={FIELD_LABEL_CLASS}>
          {t('search.sidebar.registerId', { defaultValue: 'Register ID' })}
        </Label>
        <Input
          type="text"
          inputMode="numeric"
          pattern="\d*"
          value={currentRegisterId}
          onChange={(e) =>
            setCurrentRegisterId(e.target.value.replace(/\D/g, ''))
          }
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              handleFormSubmit();
            }
          }}
          placeholder={t('search.sidebar.registerIdPlaceholder', {
            defaultValue: 'e.g. 100484',
          })}
          className={`h-11 lg:h-9 text-base lg:text-sm font-mono ${INPUT_PLACEHOLDER_CLASS}`}
        />
      </div>

      {/* Register Court — a dropdown, not free text. The backend
                matches `register_court = $1` exactly, and the stored
                spellings are the register's own: "Berlin (Charlottenburg)",
                "Frankfurt am Main", "Oldenburg (Oldenburg)". As an input
                this silently returned zero hits for anything typed the
                obvious way. Options come from the MV via
                /api/search/filters/courts, so the list can't drift from
                what is actually searchable. */}
      <div className="space-y-1.5">
        <Label className={FIELD_LABEL_CLASS}>
          {t('search.sidebar.registerCourt', {
            defaultValue: 'Register Court',
          })}
        </Label>
        <Popover
          open={registerCourtOpen}
          onOpenChange={(open) => {
            setRegisterCourtOpen(open);
            if (!open) setRegisterCourtSearch('');
          }}
        >
          <PopoverTrigger asChild>
            <Button
              variant="outline"
              role="combobox"
              className="w-full min-h-11 lg:min-h-9 h-auto font-normal text-left justify-start py-1.5 px-3 rounded-lg relative"
            >
              <div className="flex flex-wrap gap-1 w-full">
                {!currentRegisterCourt ? (
                  <span className={FIELD_PLACEHOLDER_CLASS}>
                    {t('search.sidebar.registerCourtPlaceholder', {
                      defaultValue: 'e.g. Stuttgart',
                    })}
                  </span>
                ) : (
                  <Badge
                    variant="secondary"
                    className="text-xs font-normal gap-1 pr-1 border border-foreground"
                  >
                    <span className="max-w-[220px] truncate">
                      {swissCantonLabel(currentRegisterCourt, i18n.language)}
                    </span>
                    <X
                      size={12}
                      className="cursor-pointer hover:bg-muted rounded-sm"
                      onClick={(e: React.MouseEvent) => {
                        e.stopPropagation();
                        setCurrentRegisterCourt('');
                      }}
                    />
                  </Badge>
                )}
                <ChevronDown className="absolute right-2 top-2 h-4 w-4 shrink-0 opacity-50" />
              </div>
            </Button>
          </PopoverTrigger>
          <PopoverContent
            className="w-[var(--radix-popover-trigger-width)] p-0"
            align="start"
          >
            <Command shouldFilter={false}>
              <CommandInput
                className={INPUT_PLACEHOLDER_CLASS}
                placeholder={t('search.sidebar.searchCourts', {
                  defaultValue: 'Search courts...',
                })}
                value={registerCourtSearch}
                onValueChange={setRegisterCourtSearch}
              />
              <CommandList className="max-h-[272px]">
                <CommandEmpty>
                  {t('search.sidebar.noCourtFound', {
                    defaultValue: 'No court found.',
                  })}
                </CommandEmpty>
                <CommandGroup>
                  {/* de_fold so "Duren"/"Koln" find Düren/Köln, and
                            substring so "Charlottenburg" finds the Berlin
                            entry by its suffix. */}
                  {courtList
                    .filter(
                      (c) =>
                        !registerCourtSearch ||
                        deFold(swissCantonLabel(c, i18n.language)).includes(
                          deFold(registerCourtSearch),
                        ) ||
                        deFold(c).includes(deFold(registerCourtSearch)),
                    )
                    .map((c) => (
                      <CommandItem
                        key={c}
                        value={c}
                        onSelect={() => {
                          setCurrentRegisterCourt(
                            c === currentRegisterCourt ? '' : c,
                          );
                          setRegisterCourtOpen(false);
                          setRegisterCourtSearch('');
                        }}
                      >
                        <Check
                          className={cn(
                            'mr-2 h-4 w-4',
                            currentRegisterCourt === c
                              ? 'opacity-100'
                              : 'opacity-0',
                          )}
                        />
                        <span className="truncate text-xs">
                          {swissCantonLabel(c, i18n.language)}
                        </span>
                        <FacetCount count={facetCount('register_court', c)} />
                      </CommandItem>
                    ))}
                </CommandGroup>
              </CommandList>
            </Command>
          </PopoverContent>
        </Popover>
      </div>

      {/* Register Type — HRA / HRB / PR / VR / GnR / GsR. */}
      <div className="space-y-1.5">
        <Label className={FIELD_LABEL_CLASS}>
          {t('search.sidebar.registerType', { defaultValue: 'Register Type' })}
        </Label>
        <Popover open={registerTypeOpen} onOpenChange={setRegisterTypeOpen}>
          <PopoverTrigger asChild>
            <Button
              variant="outline"
              role="combobox"
              className="w-full min-h-11 lg:min-h-9 h-auto font-normal text-left justify-start py-1.5 px-3 rounded-lg relative"
            >
              <div className="flex flex-wrap gap-1 w-full">
                {!currentRegisterType ? (
                  <span className={FIELD_PLACEHOLDER_CLASS}>
                    {t('search.sidebar.registerTypePlaceholder', {
                      defaultValue: 'e.g. HRB',
                    })}
                  </span>
                ) : (
                  <Badge
                    variant="secondary"
                    className="text-xs font-normal gap-1 pr-1 border border-foreground"
                  >
                    <span className="max-w-[220px] truncate">
                      {REGISTER_TYPE_OPTIONS.find(
                        (o) => o.value === currentRegisterType,
                      )?.label ?? currentRegisterType}
                    </span>
                    <X
                      size={12}
                      className="cursor-pointer hover:bg-muted rounded-sm"
                      onClick={(e: React.MouseEvent) => {
                        e.stopPropagation();
                        setCurrentRegisterType('');
                      }}
                    />
                  </Badge>
                )}
                <ChevronDown className="absolute right-2 top-2 h-4 w-4 shrink-0 opacity-50" />
              </div>
            </Button>
          </PopoverTrigger>
          <PopoverContent
            className="w-[var(--radix-popover-trigger-width)] p-0"
            align="start"
          >
            <Command>
              <CommandList>
                <CommandGroup>
                  {registerTypeOptions.map((opt) => (
                    <CommandItem
                      key={opt.value}
                      value={opt.value}
                      onSelect={() => {
                        setCurrentRegisterType(opt.value);
                        setRegisterTypeOpen(false);
                      }}
                    >
                      <Check
                        className={cn(
                          'mr-2 h-4 w-4',
                          currentRegisterType === opt.value
                            ? 'opacity-100'
                            : 'opacity-0',
                        )}
                      />
                      <span className="truncate text-xs">{opt.label}</span>
                      <FacetCount
                        count={facetCount('register_type', opt.value)}
                      />
                    </CommandItem>
                  ))}
                </CommandGroup>
              </CommandList>
            </Command>
          </PopoverContent>
        </Popover>
      </div>

      {/* LEI Code — exact lookup. Independent of the LEI membership
                filter under Company profile: this matches a specific 20-char
                identifier; that one matches "company has any active LEI". */}
      <div className="space-y-1.5">
        <Label className={FIELD_LABEL_CLASS}>
          {t('search.sidebar.leiCode', { defaultValue: 'LEI Code' })}
        </Label>
        <Input
          value={leiCode}
          onChange={(e) => setLeiCode(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              handleFormSubmit();
            }
          }}
          placeholder={t('search.sidebar.leiCodePlaceholder', {
            defaultValue: 'e.g. 529900T8BM49AURSDO55',
          })}
          maxLength={20}
          className={`h-11 lg:h-9 text-base lg:text-sm font-mono ${INPUT_PLACEHOLDER_CLASS}`}
        />
      </div>

      {/* USt-IdNr — German VAT number. Fixed `DE` prefix sits inside
                the field as a decorative label; the user only types digits.
                Paste of "DE123456789" naturally drops the DE since the regex
                strips every non-digit. Backend gets `DE${digits}` from
                SearchPageClient. */}
      <div className="space-y-1.5">
        <FilterLabel lock={lockOf('ustIdnr')} className={FIELD_LABEL_CLASS}>
          {t('search.sidebar.ustIdnr', { defaultValue: 'USt-IdNr.' })}
        </FilterLabel>
        <div className="relative">
          <span
            aria-hidden
            className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-base lg:text-sm font-mono text-muted-foreground select-none"
          >
            DE
          </span>
          <Input
            type="text"
            inputMode="numeric"
            pattern="\d*"
            value={ustIdnr}
            disabled={!!lockOf('ustIdnr')}
            onChange={(e) => setUstIdnr(e.target.value.replace(/\D/g, ''))}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                handleFormSubmit();
              }
            }}
            placeholder={t('search.sidebar.ustIdnrPlaceholder', {
              defaultValue: '123456789',
            })}
            className={`h-11 lg:h-9 text-base lg:text-sm font-mono pl-9 ${INPUT_PLACEHOLDER_CLASS}`}
          />
        </div>
      </div>
    </FilterGroup>
  );
}
